import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Users, Calendar, ChevronDown, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/constants/routes';
import FriendsSheet from '@/components/FriendsSheet';
import { GroupSelectionDialog } from '@/components/polls/GroupSelectionDialog';
import { TournamentGroupSelectionDialog } from '@/components/tournament/TournamentGroupSelectionDialog';
import { motion, AnimatePresence } from 'framer-motion';

// Updated padel racket icon to match reference image
const PadelRacket = () => <svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <ellipse cx="14" cy="9" rx="6" ry="7" stroke="currentColor" strokeWidth="2" fill="none" />
    <line x1="9" y1="4" x2="19" y2="4" stroke="currentColor" strokeWidth="1" />
    <line x1="9" y1="6" x2="19" y2="6" stroke="currentColor" strokeWidth="1" />
    <line x1="9" y1="8" x2="19" y2="8" stroke="currentColor" strokeWidth="1" />
    <line x1="9" y1="10" x2="19" y2="10" stroke="currentColor" strokeWidth="1" />
    <line x1="9" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="1" />
    <line x1="9" y1="14" x2="19" y2="14" stroke="currentColor" strokeWidth="1" />
    <line x1="10" y1="3" x2="10" y2="15" stroke="currentColor" strokeWidth="1" />
    <line x1="12" y1="2.5" x2="12" y2="15.5" stroke="currentColor" strokeWidth="1" />
    <line x1="14" y1="2" x2="14" y2="16" stroke="currentColor" strokeWidth="1" />
    <line x1="16" y1="2.5" x2="16" y2="15.5" stroke="currentColor" strokeWidth="1" />
    <line x1="18" y1="3" x2="18" y2="15" stroke="currentColor" strokeWidth="1" />
    <rect x="12.5" y="16" width="3" height="10" stroke="currentColor" strokeWidth="2" fill="currentColor" rx="1.5" />
    <line x1="11.5" y1="18" x2="16.5" y2="18" stroke="white" strokeWidth="0.8" />
    <line x1="11.5" y1="20" x2="16.5" y2="20" stroke="white" strokeWidth="0.8" />
    <line x1="11.5" y1="22" x2="16.5" y2="22" stroke="white" strokeWidth="0.8" />
    <line x1="11.5" y1="24" x2="16.5" y2="24" stroke="white" strokeWidth="0.8" />
  </svg>;

interface HeroActionsProps {
  user: any;
}

export const HeroActions: React.FC<HeroActionsProps> = ({ user }) => {
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [isFriendsSheetOpen, setIsFriendsSheetOpen] = useState(false);
  const [isGroupSelectionOpen, setIsGroupSelectionOpen] = useState(false);
  const [isTournamentSelectionOpen, setIsTournamentSelectionOpen] = useState(false);

  const handleAction = (action: () => void) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    action();
  };

  const primaryAction = {
    label: 'Create Match',
    icon: PadelRacket,
    action: () => navigate('/create-match'),
    primary: true
  };

  const secondaryActions = [
    {
      label: 'Create Group',
      icon: Users,
      action: () => navigate(ROUTES.CREATE_GROUP)
    },
    {
      label: 'Create Poll',
      icon: Calendar,
      action: () => setIsGroupSelectionOpen(true)
    }
  ];

  // Updated moreActions to include Create Tournament instead of Tournaments
  const moreActions = [
    {
      label: 'Create Tournament',
      icon: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      ),
      action: () => setIsTournamentSelectionOpen(true)
    },
    {
      label: 'Create League',
      icon: Trophy,
      action: () => navigate('/leagues/new')
    },
    {
      label: 'Connect',
      icon: Users,
      action: () => setIsFriendsSheetOpen(true)
    }
  ];

  const ActionButton = ({ action, className = "" }: { action: any, className?: string }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={className}
    >
      <Button
        onClick={() => handleAction(action.action)}
        className={`w-full h-auto py-4 px-6 flex flex-col items-center space-y-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 text-white transition-all duration-200 rounded-xl shadow-lg ${className}`}
        variant="ghost"
      >
        <action.icon className="h-6 w-6" />
        <span className="text-sm font-medium text-center leading-tight">{action.label}</span>
      </Button>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Primary Action - Always Visible */}
      <div className="flex justify-center">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            onClick={() => handleAction(primaryAction.action)}
            size="lg"
            className="h-16 px-8 bg-padel-green-light hover:bg-padel-green-dark text-white font-bold text-lg transition-all duration-200 shadow-xl rounded-2xl"
          >
            <PadelRacket />
            <span className="ml-2">Create Match</span>
          </Button>
        </motion.div>
      </div>

      {/* Secondary Actions */}
      <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
        {secondaryActions.map((action, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 1), duration: 0.3 }}
          >
            <ActionButton action={action} />
          </motion.div>
        ))}
      </div>

      {/* More Actions Toggle */}
      <div className="flex flex-col items-center space-y-4">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMore(!showMore)}
            className="text-white/80 hover:text-white hover:bg-white/10 transition-colors rounded-xl px-6 py-2"
          >
            More Options
            <motion.div
              animate={{ rotate: showMore ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ml-1"
            >
              <ChevronDown className="h-4 w-4" />
            </motion.div>
          </Button>
        </motion.div>

        {/* Expandable More Actions */}
        <AnimatePresence>
          {showMore && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-sm mx-auto overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-3">
                {moreActions.map((action, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.2 }}
                  >
                    <ActionButton action={action} className="h-24 text-xs" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Dialogs */}
      <FriendsSheet isOpen={isFriendsSheetOpen} onOpenChange={setIsFriendsSheetOpen} />
      <GroupSelectionDialog 
        open={isGroupSelectionOpen} 
        onOpenChange={setIsGroupSelectionOpen}
      />
      <TournamentGroupSelectionDialog
        open={isTournamentSelectionOpen}
        onOpenChange={setIsTournamentSelectionOpen}
      />
    </motion.div>
  );
};
